import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { CTASection } from "@/components/CTASection";
import { fadeUp } from "@/lib/motion";
import { useSeo } from "@/hooks/useSeo";
import { INDUSTRIES, IMAGES } from "@/data/content";

const CERTS = ["Type Approval Certification (TAC)", "Conformity of Production (COP)", "Component & System Certification", "Documentation & Submission"];

export default function Industries() {
  useSeo(
    "Industries We Serve | AutoDrive Tech Solutions",
    "Automotive certification and homologation across electric vehicles, two/three wheelers, passenger and commercial vehicles, buses, trucks, trailers, construction and agricultural vehicles."
  );

  return (
    <>
      <PageHero
        overline="Industries"
        title="Certification Across Every Vehicle Category"
        subtitle="We support the full spectrum of India's automotive segments — from electric mobility to heavy commercial platforms."
        image={IMAGES.corporate}
        crumb="Industries"
      />

      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8">
          <SectionHeading
            overline="Vehicle Categories"
            title="Applications & Relevant Certifications"
            description="Each category carries distinct regulatory requirements. We map the applicable standards and manage certification end-to-end."
            className="mb-14"
          />

          <div className="grid gap-5">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} variants={fadeUp} custom={i % 3}>
                <div
                  id={ind.name.toLowerCase().replace(/\s+/g, "-")}
                  data-testid={`industry-row-${ind.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="scroll-mt-28 group grid md:grid-cols-[auto_1fr_auto] items-center gap-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 hover:border-[#1E40AF]/30 hover:shadow-lg hover:shadow-slate-900/5 transition-[border-color,box-shadow] duration-300"
                >
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 text-slate-700 group-hover:bg-[#1E40AF] group-hover:text-white transition-colors shrink-0">
                    <ind.icon className="w-7 h-7" strokeWidth={1.5} />
                  </span>
                  <div>
                    <h3 className="font-display font-semibold text-xl text-[#0F172A]">{ind.name}</h3>
                    <p className="mt-1.5 text-slate-500 leading-relaxed max-w-2xl">{ind.text}</p>
                  </div>
                  <Link
                    to="/book-consultation"
                    className="hidden md:inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-[#0F172A] hover:border-[#1E40AF] hover:text-[#1E40AF] transition-colors shrink-0"
                  >
                    Enquire <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Relevant certifications */}
      <section className="py-24 bg-[#F8FAFC] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading overline="Applicable Approvals" title="Certifications We Deliver for Every Segment" />
            <ul className="mt-8 space-y-4">
              {CERTS.map((c) => (
                <li key={c} className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0" />
                  <span className="font-medium">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <Reveal className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10">
            <img src={IMAGES.collaborate} alt="Certification applications across vehicle categories" className="w-full h-[420px] object-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
